import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, FieldWrapper, FlexBox, Icon, InputSelectionList, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { memo, useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';
import { flowsApi } from 'apis/flows';
import { AppLink } from 'components/appLink';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { articles } from 'utils/elevio';
import { AnalyticsDocumentationButton } from '../../../../../documentation/analyticsDocumentationButton';
import { AnalyticsFilterItem, AnalyticsFilterLayout } from '../../../filter';
import styles from './analyticsBusinessHours.module.scss';
export const AnalyticsBusinessHours = memo(function AnalyticsBusinessHours({ scheduleId, onChangeSchedule, }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedScheduleId, setSelectedScheduleId] = useState(scheduleId);
    const { arg, asyncStatus, items: schedules, changeArg, hasNextPage, nextPage, } = usePaginatedEndpoint({
        endpoint: flowsApi.endpoints.readFlowsSchedules,
        initialArg: {
            limit: 50,
        },
        itemsSelector: ({ data }) => data.schedules,
        infinite: true,
    });
    // The currently selected schedule. The query refetches when `selectedScheduleId` changes, and
    // if previously cached, the API request is skipped and the cached result is returned. The query
    // is skipped initially if no schedule is selected.
    const { data: selectedSchedule, isFetching } = flowsApi.useReadFlowScheduleQuery(selectedScheduleId
        ? {
            id: selectedScheduleId,
        }
        : skipToken);
    // The selected schedule is fetched and updated when `selectedScheduleId` changes. Once
    // fulfilled, the full selected schedule object is passed to the parent component.
    useUpdateEffect(() => {
        if (!selectedScheduleId) {
            onChangeSchedule(undefined);
        }
        else if (selectedScheduleId === (selectedSchedule === null || selectedSchedule === void 0 ? void 0 : selectedSchedule.id)) {
            onChangeSchedule(selectedSchedule);
        }
    }, [selectedSchedule, selectedScheduleId]);
    useUpdateEffect(() => {
        changeArg({ searchTerm, offset: 0 }, { debounceTime: 300 });
    }, [changeArg, searchTerm]);
    const handleToggleDropdown = useCallback((isOpen) => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, []);
    /**
     * Handles selecting and de-selecting the schedule id.
     *
     * When selecting a schedule, an API request is triggered to fetch the full
     * schedule object to be passed to the parent component.
     */
    const handleChangeSchedule = useCallback((event) => {
        const [scheduleId] = event.selectedValues;
        setSelectedScheduleId(scheduleId);
    }, []);
    /** Handles rendering of the selected schedule in the input field. */
    const renderSelectedSchedule = useCallback(() => {
        if (!selectedSchedule || isFetching)
            return 'Loading...';
        return (_jsxs("div", Object.assign({ className: styles.selectedSchedule }, { children: [_jsx(Text.Paragraph, { children: selectedSchedule.name }), _jsx(Tooltip, Object.assign({ content: "Open schedule", dark: true }, { children: _jsx(AppLink, Object.assign({ path: "/settings/businesshours/:id", params: {
                            path: { id: selectedSchedule.id },
                        }, target: "_blank" }, { children: _jsx(Icon, { name: "external-link" }) })) })), _jsx(Badge, Object.assign({ variant: "secondary", size: "small" }, { children: selectedSchedule.timezone }))] })));
    }, [isFetching, selectedSchedule]);
    const renderVersionNotice = useCallback(() => {
        if (!selectedScheduleId ||
            !selectedSchedule ||
            selectedSchedule.version === 1) {
            return null;
        }
        return (_jsx(Tooltip, Object.assign({ dark: true, content: _jsxs(Text.Paragraph, Object.assign({ color: "white" }, { children: ["Analytics will reflect the business hours that were in place", ' ', _jsx("b", { children: "during the period you are looking at" }), "."] })), className: styles.tooltipNotice, wrapProps: { className: styles.tooltipNoticeWrapper } }, { children: _jsx(Icon, { name: "exclamation", size: 16, color: colors.blue[400] }) })));
    }, [selectedSchedule, selectedScheduleId]);
    const renderNewBadge = useCallback(() => {
        // Stop rendering the new badge after September 30, 2023.
        if (new Date() > new Date(2023, 8, 30))
            return null;
        return (_jsx(Badge, Object.assign({ variant: "primary", size: "small" }, { children: "New" })));
    }, []);
    return (_jsx(FieldWrapper, Object.assign({ label: _jsxs(FlexBox, Object.assign({ gap: "200", alignItems: "center" }, { children: [renderNewBadge(), _jsx("span", { children: "Business hours" }), _jsx(AnalyticsDocumentationButton, { type: "article", articleId: articles.analytics.businessHours }), renderVersionNotice()] })), labelFor: "none", noAlert: true }, { children: _jsxs(InputSelectionList, Object.assign({ value: selectedScheduleId ? [selectedScheduleId] : [], placeholder: "Select a schedule", clearable: true, onChange: handleChangeSchedule, onToggleDropdown: handleToggleDropdown, filter: {
                value: searchTerm,
                onChange: (e) => setSearchTerm(e.target.value),
            }, getItemTitleByValue: renderSelectedSchedule, dropdownProps: { className: styles.dropdown }, "data-testid": analyticsBusinessHoursTestId }, { children: [schedules.map((schedule) => (_jsx(AnalyticsFilterItem, Object.assign({ label: schedule.name, value: schedule.id, highlight: arg === null || arg === void 0 ? void 0 : arg.searchTerm, contentClassName: styles.itemContent, noFixedSelectionStateSpace: true }, { children: _jsx(Badge, Object.assign({ variant: "secondary", size: "small" }, { children: schedule.timezone })) }), schedule.id))), asyncStatus === 'pending' && _jsx(AnalyticsFilterLayout.Loading, {}), asyncStatus === 'fulfilled' && !schedules.length && (_jsx(AnalyticsFilterLayout.Empty, {})), asyncStatus === 'fulfilled' && hasNextPage && (_jsx(Waypoint, { onEnter: nextPage }))] })) })));
});
export const analyticsBusinessHoursTestId = 'analytics-business-hours';
